import { apiRequest } from '../../../shared/api';
import {
  Investigation,
  InvestigationType,
  InvestigationStatus,
  InvestigationPriority,
  InvestigationDashboardStats,
  InvestigationTemplate,
  InvestigationLinkedReport,
} from './types';

export interface InvestigationListResponse {
  items: Investigation[];
  total: number;
}

export const investigationService = {
  // Templates
  getTemplates: () => apiRequest<InvestigationTemplate[]>('/investigations/templates'),
  getMyTemplates: () => apiRequest<InvestigationTemplate[]>('/investigations/templates/my-templates'),
  getAllocations: () => apiRequest<any[]>('/investigations/templates/allocations'),
  allocateTemplate: (templateId: string, userId: string) =>
    apiRequest<any>(`/investigations/templates/${templateId}/allocate`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  deallocateTemplate: (templateId: string, userId: string) =>
    apiRequest<any>(`/investigations/templates/${templateId}/allocate/${userId}`, {
      method: 'DELETE',
    }),

  // Link to Report (Reporting Module Parity)
  getLinkableReports: (search?: string, dateFrom?: string) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (dateFrom) params.append('dateFrom', dateFrom);
    return apiRequest<any[]>(`/investigations/linkable-reports?${params.toString()}`);
  },

  // Linked Reports (Task SCRUM-64)
  getLinkedReports: (id: string) =>
    apiRequest<InvestigationLinkedReport[]>(`/investigations/${id}/reports`),
  linkReport: (id: string, data: { reportId: string; reportNumber?: string; isPrimary?: boolean; notes?: string }) =>
    apiRequest<InvestigationLinkedReport>(`/investigations/${id}/reports`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  unlinkReport: (id: string, reportId: string) =>
    apiRequest<any>(`/investigations/${id}/reports/${reportId}`, {
      method: 'DELETE',
    }),

  // Dashboard Stats
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<InvestigationDashboardStats>(`/investigations/analytics/dashboard?organizationId=${organizationId}`),

  // List
  getInvestigations: (params: {
    organizationId?: string;
    status?: InvestigationStatus | InvestigationStatus[];
    type?: InvestigationType;
    priority?: InvestigationPriority;
    leadInvestigatorId?: string;
    searchTerm?: string;
    forLinkage?: boolean;
    scope?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach(s => query.append('status', s));
      } else {
        query.append('status', params.status);
      }
    }
    if (params.type) query.append('type', params.type);
    if (params.priority) query.append('priority', params.priority);
    if (params.leadInvestigatorId) query.append('leadInvestigatorId', params.leadInvestigatorId);
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    if (params.forLinkage) query.append('for_linkage', 'true');
    if (params.scope) query.append('scope', params.scope);
    if (params.limit) query.append('limit', String(params.limit));
    if (params.offset) query.append('offset', String(params.offset));

    return apiRequest<InvestigationListResponse>(`/investigations?${query.toString()}`);
  },

  // Get Detail
  getInvestigationById: (id: string) => apiRequest<Investigation>(`/investigations/${id}`),

  // Create
  createInvestigation: (data: Partial<Investigation>) =>
    apiRequest<Investigation>('/investigations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update
  updateInvestigation: (id: string, data: Partial<Investigation>) =>
    apiRequest<Investigation>(`/investigations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Transition Status
  transitionStatus: (id: string, toStatus: InvestigationStatus, reason: string) =>
    apiRequest<Investigation>(`/investigations/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason }),
    }),

  // Team
  addTeamMember: (
    id: string,
    userId: string,
    userName: string,
    teamRole: string,
    department: string
  ) =>
    apiRequest<any>(`/investigations/${id}/team`, {
      method: 'POST',
      body: JSON.stringify({ userId, userName, teamRole, department }),
    }),

  removeTeamMember: (id: string, userId: string) =>
    apiRequest<any>(`/investigations/${id}/team/${userId}`, {
      method: 'DELETE',
    }),

  // Evidence
  addEvidence: (id: string, title: string, storageKey: string, description?: string, rawContent?: string) =>
    apiRequest<any>(`/investigations/${id}/evidence`, {
      method: 'POST',
      body: JSON.stringify({ title, storageKey, description, rawContent }),
    }),

  // Confidential Interviews
  recordInterview: (
    id: string,
    witnessCode: string,
    witnessRole: string,
    transcriptText: string,
    isConfidential: boolean = true
  ) =>
    apiRequest<any>(`/investigations/${id}/interviews`, {
      method: 'POST',
      body: JSON.stringify({ witnessCode, witnessRole, transcriptText, isConfidential }),
    }),

  // Methodologies
  saveMethodologyAnalysis: (
    id: string,
    methodologyType: string,
    analysisData: Record<string, any>,
    conclusions?: string
  ) =>
    apiRequest<any>(`/investigations/${id}/methodologies`, {
      method: 'POST',
      body: JSON.stringify({ methodologyType, analysisData, conclusions }),
    }),

  // Findings
  addFinding: (id: string, statement: string, evidenceReference?: string) =>
    apiRequest<any>(`/investigations/${id}/findings`, {
      method: 'POST',
      body: JSON.stringify({ statement, evidenceReference }),
    }),

  // Root Causes
  addRootCause: (
    id: string,
    causeCategory: string,
    description: string,
    probabilityLevel: string = 'PROBABLE',
    systemicIndicator: boolean = false
  ) =>
    apiRequest<any>(`/investigations/${id}/root-causes`, {
      method: 'POST',
      body: JSON.stringify({ causeCategory, description, probabilityLevel, systemicIndicator }),
    }),

  // Contributing Factors
  addContributingFactor: (
    id: string,
    factorType: string,
    description: string,
    impactLevel: string = 'MAJOR'
  ) =>
    apiRequest<any>(`/investigations/${id}/contributing-factors`, {
      method: 'POST',
      body: JSON.stringify({ factorType, description, impactLevel }),
    }),

  // Recommendations
  addRecommendation: (
    id: string,
    titleOrData: any,
    description?: string,
    targetDepartment?: string,
    priority: string = 'HIGH'
  ) =>
    apiRequest<any>(`/investigations/${id}/recommendations`, {
      method: 'POST',
      body: JSON.stringify(
        typeof titleOrData === 'object'
          ? titleOrData
          : { title: titleOrData, description, targetDepartment, priority }
      ),
    }),

  // Mitigation & Project Leader Verification (SafetyManager365 Parity)
  recordMitigationAction: (recId: string, mitigationActionText: string) =>
    apiRequest<any>(`/investigations/recommendations/${recId}/mitigate`, {
      method: 'POST',
      body: JSON.stringify({ mitigation_action_text: mitigationActionText }),
    }),

  verifyClosureRecommendation: (recId: string, isApproved: boolean, closureComments?: string) =>
    apiRequest<any>(`/investigations/recommendations/${recId}/verify-closure`, {
      method: 'POST',
      body: JSON.stringify({ is_approved: isApproved, closure_comments: closureComments }),
    }),

  exportRecommendations: (params?: { status?: string; safety_relevant_only?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.safety_relevant_only !== undefined) q.append('safety_relevant_only', String(params.safety_relevant_only));
    return apiRequest<any[]>(`/investigations/recommendations/export?${q.toString()}`);
  },

  getRiskOverviewItems: () =>
    apiRequest<any[]>('/investigations/risk-overview-items'),

  // Review & Sign-off
  submitReview: (id: string, verdict: 'APPROVED' | 'REVISION_REQUESTED', comments: string) =>
    apiRequest<any>(`/investigations/${id}/review`, {
      method: 'POST',
      body: JSON.stringify({ verdict, comments }),
    }),

  // Final Report
  getFinalReportMarkdown: (id: string) =>
    apiRequest<{ markdown: string }>(`/investigations/${id}/report`),
};
